import { Link, useParams } from 'react-router-dom'

import { PageState } from '../app/PublicLayout.jsx'
import { assetUrl, useRecord } from '../app/useSiteData.js'
import { BlockRenderer } from '../blocks/index.js'
import { useI18n } from '../lib/i18n.jsx'
import {
  Chip,
  Container,
  DefinitionList,
  EmptyState,
  Media,
  Prose,
  Row,
  Section,
} from '../ui/index.js'

/**
 * One research direction.
 *
 * A document, like the news detail page, but opening with the facts instead of
 * a date: funder, grant number, period, amount, role, progress. A definition
 * list is the right shape for that — six label/value pairs a reader scans
 * vertically, not six sentences they have to read.
 *
 * Everything below the facts is the editor's block document. This page never
 * decides what a heading or a figure looks like; that is `BlockRenderer`'s job
 * and it is the same renderer the editor previews with, so what is written is
 * what is published.
 *
 * 单个研究方向。
 *
 * 和新闻详情一样是一篇文档，但开头给的是事实而不是日期：资助来源、项目编号、
 * 起止时间、经费、承担角色、进展。定义列表正是这种内容的形状——六组标签/值供读者
 * 纵向扫读，而不是六句话要人逐字读。
 *
 * 事实之下的一切都是编辑的块文档。本页从不决定标题或配图长什么样，那是 BlockRenderer
 * 的职责，而且编辑器预览用的是同一个渲染器——写成什么样，发出来就是什么样。
 */

/** UI string key per project role. 承担角色对应的文案 key。 */
const ROLE_LABEL_KEY = { pi: 'research.rolePi', copi: 'research.roleCopi' }

/** UI string key per project progress. 项目进展对应的文案 key。 */
const STATUS_LABEL_KEY = { ongoing: 'research.ongoing', finished: 'research.finished' }

/**
 * Research detail page.
 *
 * 研究方向详情页。
 *
 * @returns {JSX.Element} Page / 页面。
 */
export default function ResearchDetail() {
  const { id } = useParams()
  const { t, pick } = useI18n()
  const { data: record, loading, error, reload } = useRecord('research', id)

  const missing = error && error.status === 404
  const title = record ? pick(record.title) : ''
  const summary = record ? pick(record.summary) : ''
  const figure = record ? assetUrl(record.cover) : ''
  const keywords = record ? pick(record.keywords) : []

  // DefinitionList drops rows whose value is falsy, so an unfunded direction
  // simply shows fewer facts rather than a column of empty dashes.
  // DefinitionList 会丢掉值为假的行，因此没有立项信息的方向只是少显示几项，
  // 而不是排出一列空破折号。
  const facts = record
    ? [
        { label: t('research.pi'), value: pick(record.pi) },
        { label: t('research.funder'), value: pick(record.funder) },
        { label: t('research.grantNo'), value: record.grantNo },
        { label: t('research.period'), value: record.years },
        { label: t('research.amount'), value: record.amount },
        { label: t('research.role'), value: ROLE_LABEL_KEY[record.role] && t(ROLE_LABEL_KEY[record.role]) },
        {
          label: t('research.status'),
          value: STATUS_LABEL_KEY[record.projectStatus] && t(STATUS_LABEL_KEY[record.projectStatus]),
        },
      ]
    : []

  return (
    <Section className="research-detail">
      <Container width="narrow">
        <nav className="detail-breadcrumb" aria-label={t('research.title')}>
          <Link className="detail-breadcrumb__link" to="/research/projects">
            {t('research.title')}
          </Link>
        </nav>

        <PageState
          loading={loading}
          error={missing ? null : error}
          onRetry={reload}
          isEmpty={missing || !record}
          empty={
            <EmptyState
              title={t('research.notFound')}
              body={t('error.notFoundBody')}
              action={
                <Link className="link-button link-button--ghost" to="/research">
                  {t('research.backToList')}
                </Link>
              }
            />
          }
        >
          {record && (
            <article className="research-article">
              <header className="research-article__head">
                <h1 className="research-article__title">{title}</h1>
                {summary && <p className="research-article__lede">{summary}</p>}
                {Array.isArray(keywords) && keywords.length > 0 && (
                  <Row className="research-article__keywords" gap="tight">
                    {keywords.map((keyword) => (
                      <Chip key={keyword} className="research-article__keyword">
                        {keyword}
                      </Chip>
                    ))}
                  </Row>
                )}
              </header>

              <DefinitionList className="research-article__facts" items={facts} />

              {figure && (
                <Media className="research-article__figure">
                  <img src={figure} alt={title} loading="lazy" decoding="async" />
                </Media>
              )}

              <Prose className="research-article__body">
                <BlockRenderer blocks={record.blocks} />
              </Prose>
            </article>
          )}
        </PageState>
      </Container>
    </Section>
  )
}
